import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Landing page after Stripe checkout success.
 * Polls /api/payments/status/{session_id} until paid, then activates
 * (creates/logs-in a reader account tied to the buyer's email) and
 * redirects back to the coloring page with the paywall unlocked.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | error
  const [message, setMessage] = useState("Confirming your purchase…");
  const [bookId, setBookId] = useState(null);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!sessionId) { setStatus("error"); setMessage("Missing session id."); return; }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          // Activate: create/find reader account by email + set session cookie
          const activated = await api.post(`/payments/activate/${sessionId}`);
          if (cancelled) return;
          const bundleIds = Array.isArray(activated.data.bundle_book_ids) ? activated.data.bundle_book_ids : [];
          const isBundle = bundleIds.length > 0;
          setBookId(activated.data.book_id);
          setStatus("paid");
          setMessage(isBundle ? "The whole bundle is yours! Redirecting…" : "Your book is unlocked! Redirecting…");
          try { await refresh?.(); } catch { /* ignore */ }
          if (isBundle) {
            // Mark every book unlocked client-side
            try {
              bundleIds.forEach((id) => localStorage.setItem(`mkb-coloring-purchased-${id}`, "yes"));
            } catch { /* ignore */ }
          }
          setTimeout(() => {
            if (isBundle) {
              navigate("/coloring?bundle_unlocked=1", { replace: true });
            } else if (activated.data.book_id) {
              navigate(`/coloring/${activated.data.book_id}?unlocked=1`, { replace: true });
            } else {
              navigate("/my/library", { replace: true });
            }
          }, 900);
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          setMessage("Payment didn't go through — no charge was made.");
          return;
        }
        if (Date.now() - startedAt.current > 60000 && attempts > 20) {
          setStatus("error");
          setMessage("This is taking longer than usual — Stripe may still be processing. Please refresh in a moment.");
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage("Something went wrong confirming your purchase. Please refresh, or contact hello@mkbunchbooks.com.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate, refresh]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      <span
        className="inline-flex w-16 h-16 rounded-3xl items-center justify-center mb-6"
        style={{
          background: status === "paid" ? "linear-gradient(135deg,#66D9A6 0%,#3FB07B 100%)"
                     : "linear-gradient(135deg,#FFD93D 0%,#FF8C42 100%)",
          color: "#FFFFFF",
        }}
      >
        {status === "paid" ? <CheckCircle2 size={30} /> : <Loader2 size={30} className="animate-spin" />}
      </span>
      <h1 className="font-fredoka text-4xl sm:text-5xl" style={{ color: "#2D3055" }}>
        {status === "paid" ? "You're all set! 🎨"
          : status === "failed" ? "Payment didn't go through"
          : status === "error"  ? "Hmm, one moment…"
          : "Confirming your purchase…"}
      </h1>
      <p className="mt-4 font-nunito text-lg" style={{ color: "#4A4E6D" }}>{message}</p>

      {(status === "failed" || status === "error") && (
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link
            to={bookId ? `/coloring/${bookId}` : "/books"}
            className="btn-ghost-warm inline-flex"
            data-testid="payment-success-back"
          >
            <ArrowLeft size={14} /> Back to the coloring pages
          </Link>
        </div>
      )}
    </div>
  );
}
